import { useState, useEffect, useCallback } from 'react';
import axiosClient from './lib/axiosClient';
import ProductGroupEditor from './ProductGroupEditor';
import VariantManager from './VariantManager';
import CategoryTree from './CategoryTree';
import ColumnManager from './ColumnManager';
import BarcodeScanner from './BarcodeScanner';

const STATUS_DOT = { available: '#4ade80', rented: '#facc15', maintenance: '#fb923c', repair: '#f87171', retired: '#6b7280' };

export default function InventoryBrowser() {
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selectedCat, setSelectedCat] = useState(null); // null = all/tiles
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode]     = useState('grid'); // 'grid' | 'list'
  const [view, setView]             = useState('browse'); // 'browse' | 'editor' | 'variants'
  const [activeId, setActiveId]     = useState(null);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [customFilters, setCustomFilters] = useState({});
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [inlineEdit, setInlineEdit] = useState(null); // {prodId, fieldKey, value, fieldDef}
  const [inlineSaving, setInlineSaving] = useState(false);

  const saveInlineEdit = async () => {
    if (!inlineEdit) return;
    setInlineSaving(true);
    try {
      const prod = products.find(p => p.id === inlineEdit.prodId);
      if (prod) {
        for (const v of (prod.variants || [])) {
          const newVals = { ...(v.property_values || {}), [inlineEdit.fieldKey]: inlineEdit.value };
          await axiosClient.patch(`/api/rentals/products/variants/${v.id}`, { property_values: newVals });
        }
        // Update local state immediately without full reload
        setProducts(prev => prev.map(p => p.id === inlineEdit.prodId ? {
          ...p,
          variants: p.variants.map(v => ({
            ...v,
            property_values: { ...(v.property_values||{}), [inlineEdit.fieldKey]: inlineEdit.value }
          }))
        } : p));
      }
    } catch(e) { console.error('Inline save failed', e); }
    setInlineSaving(false);
    setInlineEdit(null);
  };

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      axiosClient.get('/api/rentals/products'),
      axiosClient.get('/api/rentals/categories'),
    ]).then(([pr, cr]) => {
      const prods = (pr.data.products || []).map(p => ({ ...p, variants: p.rental_product_variants || p.variants || [] }));
      setProducts(prods);
      setCategories(cr.data.categories || []);
    }).catch((e) => { console.error('Inventory load failed:', e?.response?.status, e?.response?.data || e.message); setError('Failed to load inventory.'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openEditor   = (id = null) => { setActiveId(id); setView('editor'); };
  const openVariants = (id) => { setActiveId(id); setView('variants'); };
  const backToBrowse = () => { setView('browse'); setActiveId(null); loadAll(); };

  const handleBarcodeScan = (rawValue) => {
    setShowScanner(false);
    const val = rawValue.trim().toLowerCase();
    const product = products.find(p => (p.variants || []).some(v =>
      (v.serial_number && v.serial_number.toLowerCase() === val) ||
      (v.sku && v.sku.toLowerCase() === val) ||
      (v.barcode && v.barcode.toLowerCase() === val)
    ));
    if (product) {
      openEditor(product.id);
    } else {
      alert('No equipment found with that barcode');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axiosClient.delete(`/api/rentals/products/${id}`);
      loadAll();
    } catch(e) {
      setError(e?.response?.data?.error || 'Delete failed.');
    }
  };

  if (view === 'editor') {
    return <ProductGroupEditor
      groupId={activeId}
      categories={categories}
      defaultCategoryId={selectedCat?.id !== '__uncategorized__' ? selectedCat?.id : null}
      onSaved={backToBrowse}
      onCancel={backToBrowse}
    />;
  }

  if (view === 'variants') {
    const product = products.find(p => p.id === activeId);
    const productCat = categories.find(c => c.id === product?.category_id);
    const mergedSchema = [
      ...(productCat?.custom_field_schema || []),
      ...(product?.property_schema || []),
    ];
    return <VariantManager
      productGroupId={activeId}
      productName={product?.name || ''}
      propertySchema={mergedSchema}
      productType={product?.product_type || 'rental'}
      onClose={backToBrowse}
    />;
  }

  // Filter products for main panel
  const searchLower = search.toLowerCase().trim();
  const searchMatches = searchLower ? products.filter(p => {
    const catName = categories.find(c => c.id === p.category_id)?.name || '';
    return p.name?.toLowerCase().includes(searchLower)
      || (p.description || '').toLowerCase().includes(searchLower)
      || catName.toLowerCase().includes(searchLower)
      || (p.variants || []).some(v =>
          (v.serial_number || '').toLowerCase().includes(searchLower) ||
          (v.sku || '').toLowerCase().includes(searchLower)
        );
  }) : null;

  const baseProds = searchMatches || (selectedCat
    ? selectedCat.id === '__uncategorized__'
      ? products.filter(p => !p.category_id)
      : products.filter(p => p.category_id === selectedCat.id)
    : products);
  const displayProds = Object.keys(customFilters).some(k=>customFilters[k])
    ? baseProds.filter(p => Object.entries(customFilters).every(([k,v]) => {
        if (!v) return true;
        return p.variants.some(variant => {
          const val = String((variant.property_values||{})[k]||'');
          return val.toLowerCase() === v.toLowerCase();
        });
      }))
    : baseProds;

  const totalUnits = products.reduce((s, p) => s + p.variants.length, 0);
  const totalAvail = products.reduce((s, p) => s + p.variants.filter(v => v.unit_status === 'available').length, 0);
  const rootCats   = categories.filter(c => !c.parent_id);
  const uncatProds = products.filter(p => !p.category_id);

  return (
    <div style={{ display: 'flex', gap: 0, color: '#e0e0f0', fontFamily: 'sans-serif', minHeight: 0 }}>

      {/* ── LEFT SIDEBAR ── */}
      <div style={S.sidebar}>
        <div style={S.sidebarTitle}>CATEGORIES</div>

        {/* All Equipment */}
        <div
          style={{ ...S.sidebarItem, ...(selectedCat === null ? S.sidebarItemActive : {}) }}
          onClick={() => setSelectedCat(null)}
        >
          <span>🗂️</span>
          <span style={{ flex: 1 }}>All Equipment</span>
          <span style={S.sidebarCount}>{products.length}</span>
        </div>

        {/* Category tree */}
        <CategoryTree
          categories={categories}
          products={products}
          selectedId={selectedCat?.id || null}
          onSelect={cat => setSelectedCat(cat)}
          onRefresh={loadAll}
          mode="manage"
          showUncategorized={true}
          activeProductId={selectedProduct?.id || null}
          onSelectProduct={p => {
            const cat = categories.find(c => c.id === p.category_id) || null;
            setSelectedCat(cat);
            setSelectedProduct(p);
          }}
        />
      </div>

      {/* ── MAIN PANEL ── */}
      <div style={S.main}>
        {/* Top bar */}
        <div style={S.topBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {selectedCat && (
              <button onClick={() => setSelectedCat(null)} style={{ background: 'none', border: 'none', color: '#f97316', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                ← All Equipment
              </button>
            )}
            <div style={S.pageTitle}>
              {selectedCat ? selectedCat.name : 'All Equipment'}
            </div>
            <div style={S.pageSub}>
              {selectedCat
                ? `${displayProds.length} products · ${displayProds.reduce((s,p)=>s+p.variants.length,0)} units`
                : `${products.length} products · ${totalUnits} units · ${totalAvail} available`
              }
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, maxWidth: 320, marginRight: 8 }}>
            <input
              style={{ background: '#0f0f20', border: '1px solid #2a2a4a', borderRadius: 6, padding: '7px 12px', color: '#e0e0f0', fontSize: 13, width: '100%' }}
              placeholder="🔍  Search products, serials, SKUs..."
              value={search}
              onChange={e => { setSearch(e.target.value); if (e.target.value) setSelectedCat(null); }}
            />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>✕</button>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={S.toggleGroup}>
              <button onClick={() => setViewMode('grid')} style={{ ...S.toggleBtn, ...(viewMode==='grid' ? S.toggleActive : {}) }} title="Grid view">⊞</button>
              <button onClick={() => setViewMode('list')} style={{ ...S.toggleBtn, ...(viewMode==='list' ? S.toggleActive : {}) }} title="List view">☰</button>
            </div>
            {selectedCat && selectedCat.id !== '__uncategorized__' && (
              <button onClick={()=>setShowColumnManager(true)} style={{...S.primaryBtn, background:'#1a1a3a', color:'#a0a0ff', border:'1px solid #2a2a5a'}}>⚙️ Manage Columns</button>
            )}
            <button onClick={() => setShowScanner(true)} style={{...S.primaryBtn, background:'#1a1a3a', color:'#a0a0ff', border:'1px solid #2a2a5a'}}>📷 Scan</button>
            <button onClick={() => openEditor(null)} style={S.primaryBtn}>+ New Product</button>
            {showScanner && (
              <BarcodeScanner
                onRawScan={handleBarcodeScan}
                onClose={() => setShowScanner(false)}
              />
            )}
            {showColumnManager && selectedCat && selectedCat.id !== '__uncategorized__' && (
              <ColumnManager
                category={selectedCat}
                onSave={async (schema) => {
                  await axiosClient.patch('/api/rentals/categories/' + selectedCat.id, { custom_field_schema: schema });
                  await loadAll();
                  setSelectedCat(cats => {
                    const updated = categories.find(c => c.id === selectedCat.id);
                    return updated ? {...selectedCat, custom_field_schema: schema} : selectedCat;
                  });
                }}
                onClose={()=>setShowColumnManager(false)}
              />
            )}
          </div>
        </div>

        {/* Category tabs */}
        {!search && (
          <div style={{display:'flex',gap:0,borderBottom:'1px solid #252840',marginBottom:16,overflowX:'auto',flexShrink:0}}>
            <div onClick={()=>{setSelectedCat(null);setCustomFilters({});}}
              style={{padding:'8px 16px',fontSize:12,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',color:selectedCat===null?'#f97316':'#555',borderBottom:selectedCat===null?'2px solid #f97316':'2px solid transparent',marginBottom:-1}}>
              All ({products.length})
            </div>
            {categories.filter(c=>!c.parent_id).map(cat=>{
              const count = products.filter(p=>p.category_id===cat.id).length;
              return (
                <div key={cat.id} onClick={()=>{setSelectedCat(cat);setCustomFilters({});}}
                  style={{padding:'8px 16px',fontSize:12,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',color:selectedCat?.id===cat.id?'#f97316':'#555',borderBottom:selectedCat?.id===cat.id?'2px solid #f97316':'2px solid transparent',marginBottom:-1,display:'flex',alignItems:'center',gap:6}}>
                  {cat.name} <span style={{fontSize:10,color:'#333'}}>({count})</span>

                </div>
              );
            })}
          </div>
        )}

        {/* Custom field filters */}
        {selectedCat && (selectedCat.custom_field_schema||[]).length > 0 && (
          <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
            <span style={{fontSize:11,color:'#555',fontWeight:600}}>FILTER:</span>
            {(selectedCat.custom_field_schema||[]).filter(f=>f.type==='select'||f.type==='boolean').map(f=>(
              <select key={f.key} value={customFilters[f.key]||''} onChange={e=>setCustomFilters(p=>({...p,[f.key]:e.target.value}))}
                style={{background:'#0f0f20',border:'1px solid #2a2a4a',borderRadius:6,padding:'5px 8px',color:'#e0e0f0',fontSize:11}}>
                <option value="">All {f.label}</option>
                {f.type==='boolean' && <><option value="true">Yes</option><option value="false">No</option></>}
                {f.type==='select' && (f.options||[]).map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            {Object.values(customFilters).some(Boolean) &&
              <button onClick={()=>setCustomFilters({})} style={{background:'none',border:'1px solid #333',borderRadius:4,color:'#888',fontSize:11,padding:'4px 8px',cursor:'pointer'}}>✕ Clear</button>}
          </div>
        )}

        {/* Subcategories — folder tiles, shown when browsing a parent category */}
        {selectedCat && !search && selectedCat.id !== '__uncategorized__' && (() => {
          const subCats = categories.filter(c => c.parent_id === selectedCat.id);
          if (subCats.length === 0) return null;
          return (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#555', textTransform: 'uppercase', marginBottom: 10 }}>Subcategories</div>
              <div style={S.catGrid}>
                {subCats.map(sub => (
                  <div key={sub.id} style={S.catTile} onClick={() => setSelectedCat(sub)}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>📁</div>
                    <div style={S.catTileName}>{sub.name}</div>
                    <div style={S.catTileStats}>{products.filter(p => p.category_id === sub.id).length} products</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#555', textTransform: 'uppercase', marginTop: 20, marginBottom: 10 }}>Products in {selectedCat.name}</div>
            </div>
          );
        })()}

        {error && <div style={S.error}>{error} <button onClick={() => setError(null)} style={{ background:'none',border:'none',color:'#ff8080',cursor:'pointer' }}>✕</button></div>}

        {loading && <div style={S.loading}>Loading...</div>}

        {search && !loading && (
          <div style={{ marginBottom: 12, color: '#888', fontSize: 13 }}>
            {(searchMatches||[]).length} result{(searchMatches||[]).length !== 1 ? 's' : ''} for <span style={{ color: '#fff' }}>"{search}"</span>
          </div>
        )}
        {/* All Equipment — show category tiles */}
        {!loading && !selectedCat && !search && (
          <div style={S.catGrid}>
            {rootCats.map(cat => {
              const catProds = products.filter(p => p.category_id === cat.id);
              const units = catProds.reduce((s,p)=>s+p.variants.length,0);
              const avail = catProds.reduce((s,p)=>s+p.variants.filter(v=>v.unit_status==='available').length,0);
              return (
                <div key={cat.id} style={S.catTile} onClick={() => setSelectedCat(cat)}>
                  <div style={{ position: 'relative', width: '100%', height: 80, marginBottom: 10, borderRadius: 6, overflow: 'hidden', background: '#0f0f20' }}>
                    {cat.photo_url
                      ? <img src={cat.photo_url} alt={cat.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>📦</div>
                    }
                    <label
                      onClick={e => e.stopPropagation()}
                      style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: '1px solid #333', borderRadius: 4, padding: '2px 6px', fontSize: 10, color: '#aaa', cursor: 'pointer' }}
                    >
                      📷
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const { createClient } = await import('@supabase/supabase-js');
                        const sb = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);
                        const ext = file.name.split('.').pop();
                        const filePath = 'categories/' + cat.id + '.' + ext;
                        await sb.storage.from('rental-photos').upload(filePath, file, { upsert: true });
                        const { data: { publicUrl } } = sb.storage.from('rental-photos').getPublicUrl(filePath);
                        await axiosClient.patch('/api/rentals/categories/' + cat.id, { photo_url: publicUrl });
                        loadAll();
                      }} />
                    </label>
                  </div>
                  <div style={S.catTileName}>{cat.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={S.catTileStats}>{catProds.length} products · {units} units</div>
                    <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                      <button onClick={e => { e.stopPropagation(); const n = window.prompt('Rename category:', cat.name); if (n && n.trim() && n !== cat.name) axiosClient.patch('/api/rentals/categories/' + cat.id, { name: n.trim() }).then(loadAll); }} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 12, padding: '2px 4px' }} title="Rename">✏️</button>
                      <button onClick={e => { e.stopPropagation(); if (window.confirm('Delete "' + cat.name + '"? Products will move to Uncategorized.')) { axiosClient.patch('/api/rentals/categories/' + cat.id + '/unassign-products').catch(()=>{}).finally(() => axiosClient.delete('/api/rentals/categories/' + cat.id).then(loadAll)); } }} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 12, padding: '2px 4px' }} title="Delete">🗑️</button>
                    </div>
                  </div>
                  {units > 0 && (
                    <>
                      <div style={{ height: 3, background: '#252840', borderRadius: 2, marginTop: 10, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${units>0?(avail/units)*100:0}%`, background: '#4ade80', borderRadius: 2 }} />
                      </div>
                      <div style={{ fontSize: 11, color: '#4ade80', marginTop: 4 }}>{avail}/{units} available</div>
                    </>
                  )}
                </div>
              );
            })}
            {uncatProds.length > 0 && (
              <div style={{ ...S.catTile, borderStyle: 'dashed' }} onClick={() => setSelectedCat({ id: '__uncategorized__', name: 'Uncategorized' })}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
                <div style={{ ...S.catTileName, color: '#888' }}>Uncategorized</div>
                <div style={S.catTileStats}>{uncatProds.length} products</div>
              </div>
            )}
            {rootCats.length === 0 && uncatProds.length === 0 && (
              <div style={{ color: '#444', fontSize: 14, gridColumn: '1/-1', textAlign: 'center', padding: 40 }}>
                No categories yet. Use the sidebar to create one.
              </div>
            )}
          </div>
        )}

        {/* Inside a category or search */}
        {!loading && (selectedCat || search) && (
          <>
            {displayProds.length === 0 ? (
              <div style={S.empty}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
                <div style={{ color: '#666', marginBottom: 16 }}>No products in this category.</div>
                <button onClick={() => openEditor(null)} style={S.primaryBtn}>+ Add Product</button>
              </div>
            ) : viewMode === 'grid' ? (
              <div style={S.grid}>
                {displayProds.map(p => (
                  <div key={p.id} id={`prod-${p.id}`} style={{ outline: selectedProduct?.id === p.id ? '2px solid #f97316' : 'none', borderRadius: 10 }}>
                    <ProductCard p={p}
                      catSchema={selectedCat?.custom_field_schema||[]}
                      onEdit={() => openEditor(p.id)}
                      onUnits={() => openVariants(p.id)}
                      onDelete={() => handleDelete(p.id, p.name)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <table style={S.table}>
                <thead>
                  <tr>
                    {['','Name','Type','Units','Available','Day Rate'].map(h=><th key={h} style={S.th}>{h}</th>)}
                    {(selectedCat?.custom_field_schema||[]).filter(f=>f.show_in_table!==false).map(f=><th key={f.key} style={S.th}>{f.label}</th>)}
                    <th style={S.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayProds.map(p => {
                    const avail = p.variants.filter(v=>v.unit_status==='available').length;
                    const photo = p.photo_urls?.[0];
                    return (
                      <tr key={p.id} style={S.tr}>
                        <td style={{ ...S.td, width: 44 }}>
                          {photo
                            ? <img src={photo} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: '1px solid #2a2a4a' }} />
                            : <div style={{ width: 36, height: 36, background: '#252840', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📦</div>
                          }
                        </td>
                        <td style={S.td}><div style={{ fontWeight: 600, color: '#fff' }}>{p.name}</div></td>
                        <td style={S.td}><span style={{ color: '#a0a0ff', fontSize: 12 }}>{p.product_type}</span></td>
                        <td style={S.td}><span style={{ color: '#aaa' }}>{p.variants.length}</span></td>
                        <td style={S.td}><span style={{ color: avail>0?'#4ade80':'#f87171' }}>{avail}</span></td>
                        <td style={S.td}><span style={{ color: '#4ade80' }}>{p.base_price_day!=null?`$${p.base_price_day}`:'—'}</span></td>
                        {(selectedCat?.custom_field_schema||[]).filter(f=>f.show_in_table!==false).map(f=>{
                          const vals = [...new Set(p.variants.map(v=>(v.property_values||{})[f.key]).filter(Boolean))];
                          const isEditing = inlineEdit?.prodId === p.id && inlineEdit?.fieldKey === f.key;
                          const needsSelect = ['select','multiselect','Dropdown','Multi-select'].includes(f.base_type||f.type_name) && (f.options||[]).length > 0;
                          return (
                            <td key={f.key} style={{...S.td, cursor:'pointer', minWidth:80}} onClick={()=>{ if(!isEditing) setInlineEdit({prodId:p.id, fieldKey:f.key, value:vals[0]||'', fieldDef:f}); }}>
                              {isEditing ? (
                                <div style={{display:'flex',gap:4,alignItems:'center'}} onClick={e=>e.stopPropagation()}>
                                  {needsSelect
                                    ? <select autoFocus value={inlineEdit.value}
                                        onChange={e=>setInlineEdit(p=>({...p,value:e.target.value}))}
                                        onBlur={saveInlineEdit}
                                        style={{background:'#1e2030',border:'1px solid #f97316',borderRadius:4,padding:'3px 6px',color:'#e2e8f0',fontSize:11,outline:'none'}}>
                                        <option value="">— select —</option>
                                        {(f.options||[]).map(opt=><option key={opt} value={opt}>{opt}</option>)}
                                      </select>
                                    : <input autoFocus value={inlineEdit.value}
                                        onChange={e=>setInlineEdit(p=>({...p,value:e.target.value}))}
                                        onBlur={saveInlineEdit}
                                        onKeyDown={e=>{ if(e.key==='Enter') saveInlineEdit(); if(e.key==='Escape') setInlineEdit(null); }}
                                        style={{width:80,background:'#1e2030',border:'1px solid #f97316',borderRadius:4,padding:'3px 6px',color:'#e2e8f0',fontSize:11,outline:'none'}} />
                                  }
                                  {inlineSaving && <span style={{fontSize:10,color:'#f97316'}}>...</span>}
                                </div>
                              ) : (
                                <span style={{fontSize:11,color:vals.length?'#a0f0cc':'#555',borderBottom:vals.length?'none':'1px dashed #555'}}>
                                  {vals.length ? vals.join(' / ') : '+ set'}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td style={S.td}>
                          <button onClick={()=>openVariants(p.id)} style={S.actionBtn}>Units</button>
                          <button onClick={()=>openEditor(p.id)} style={S.actionBtn}>Edit</button>
                          <button onClick={()=>handleDelete(p.id,p.name)} style={S.deleteBtn}>Del</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ProductCard({ p, onEdit, onUnits, onDelete, catSchema=[] }) {
  const variants = p.variants || [];
  const statusCounts = variants.reduce((acc,v)=>{ acc[v.unit_status]=(acc[v.unit_status]||0)+1; return acc; }, {});
  const avail = statusCounts.available || 0;
  const total = variants.length;
  const photo = p.photo_urls?.[0];
  // Build field summary from variants — only fields marked to show
  const fieldSummary = catSchema.filter(f => f.show_in_table !== false).map(f => {
    const vals = [...new Set(variants.map(v=>(v.property_values||{})[f.key]).filter(Boolean))];
    return vals.length ? { label: f.label, value: vals.join(' / ') } : null;
  }).filter(Boolean);

  return (
    <div style={S.card}>
      {photo && (
        <img src={photo} alt={p.name} style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: '6px 6px 0 0', marginBottom: 12, marginTop: -14, marginLeft: -14, width: 'calc(100% + 28px)' }} />
      )}
      <div style={{ marginBottom: 8 }}>
        <div style={S.cardName}>{p.name}</div>
        <div style={S.cardTags}>
          <span style={{ ...S.tag, color: '#a0a0ff' }}>{p.product_type}</span>
          <span style={{ ...S.tag, color: '#6aaa6a' }}>{p.tracking_mode}</span>
          {!p.is_published && <span style={{ ...S.tag, color: '#fb923c' }}>draft</span>}
        </div>
      </div>

      {total > 0 ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 11, color: '#666' }}>Availability</span>
            <span style={{ fontSize: 11, color: avail>0?'#4ade80':'#f87171' }}>{avail}/{total}</span>
          </div>
          <div style={{ height: 4, background: '#252840', borderRadius: 2, marginBottom: 8, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${total>0?(avail/total)*100:0}%`, background: avail>0?'#4ade80':'#f87171', borderRadius: 2 }} />
          </div>
          <div style={S.statusRow}>
            {Object.entries(statusCounts).map(([status,count])=>(
              <span key={status} style={S.statusChip}>
                <span style={{ ...S.dot, background: STATUS_DOT[status]||'#888' }} />
                {count} {status}
              </span>
            ))}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12, color: '#444', fontStyle: 'italic', marginBottom: 10 }}>No units yet</div>
      )}

      <div style={S.pricingRow}>
        {p.base_price_day   != null && <span style={S.priceChip}><span style={{ color:'#555' }}>Day: </span><span style={{ color:'#4ade80', fontWeight:600 }}>${parseFloat(p.base_price_day).toFixed(0)}</span></span>}
        {p.base_price_week  != null && <span style={S.priceChip}><span style={{ color:'#555' }}>Wk: </span><span style={{ color:'#4ade80', fontWeight:600 }}>${parseFloat(p.base_price_week).toFixed(0)}</span></span>}
        {p.base_price_month != null && <span style={S.priceChip}><span style={{ color:'#555' }}>Mo: </span><span style={{ color:'#4ade80', fontWeight:600 }}>${parseFloat(p.base_price_month).toFixed(0)}</span></span>}
      </div>

      {fieldSummary.length > 0 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:8}}>
          {fieldSummary.map(f=>(
            <span key={f.label} style={{fontSize:10,background:'#1a1a3a',border:'1px solid #2a2a5a',borderRadius:4,padding:'2px 6px',color:'#a0a0ff'}}>
              {f.label}: <span style={{color:'#e0e0f0',fontWeight:600}}>{f.value}</span>
            </span>
          ))}
        </div>
      )}
      <div style={S.cardActions}>
        <button onClick={onUnits} style={S.unitsBtn}>Units ({total})</button>
        <button onClick={onEdit}  style={S.editBtn}>Edit</button>
        <button onClick={onDelete} style={S.deleteCardBtn}>✕</button>
      </div>
    </div>
  );
}

const S = {
  sidebar:      { width: 220, flexShrink: 0, borderRight: '1px solid #252840', paddingRight: 12, marginRight: 16, paddingTop: 4 },
  sidebarTitle: { fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: '#444', textTransform: 'uppercase', marginBottom: 8 },
  sidebarItem:  { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', borderRadius: 6, cursor: 'pointer', marginBottom: 2, border: '1px solid transparent' },
  sidebarItemActive: { background: '#1a1a3a', border: '1px solid #3a3aff' },
  sidebarCount: { fontSize: 10, color: '#4ade80' },
  main:         { flex: 1, minWidth: 0 },
  topBar:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  pageTitle:    { fontSize: 20, fontWeight: 700, color: '#fff' },
  pageSub:      { fontSize: 12, color: '#555', marginTop: 2 },
  primaryBtn:   { background: '#4a4aff', border: 'none', color: '#fff', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  toggleGroup:  { display: 'flex', background: '#0f0f20', border: '1px solid #2a2a4a', borderRadius: 6, overflow: 'hidden' },
  toggleBtn:    { background: 'none', border: 'none', color: '#666', padding: '6px 11px', fontSize: 15, cursor: 'pointer' },
  toggleActive: { background: '#2a2a5a', color: '#a0a0ff' },
  error:        { background: '#3a1a1a', border: '1px solid #a03030', color: '#ff8080', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13, display: 'flex', justifyContent: 'space-between' },
  loading:      { color: '#555', textAlign: 'center', padding: 60 },
  empty:        { textAlign: 'center', padding: '60px 0' },
  catGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14 },
  catTile:      { background: '#252840', border: '1px solid #2a2a4a', borderRadius: 12, padding: 18, cursor: 'pointer', overflow: 'hidden', minWidth: 0 },
  catTileName:  { fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' },
  catTileStats: { fontSize: 12, color: '#555' },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  card:         { background: '#252840', border: '1px solid #2a2a4a', borderRadius: 10, padding: 14, overflow: 'hidden' },
  cardName:     { fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 5 },
  cardTags:     { display: 'flex', flexWrap: 'wrap', gap: 4 },
  tag:          { fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#2a2a3a', color: '#777' },
  pricingRow:   { marginBottom: 8, minHeight: 16 },
  priceChip:    { fontSize: 11, marginRight: 8 },
  statusRow:    { display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 },
  statusChip:   { fontSize: 10, color: '#777', display: 'flex', alignItems: 'center', gap: 3 },
  dot:          { width: 6, height: 6, borderRadius: '50%', display: 'inline-block' },
  cardActions:  { display: 'flex', gap: 5, borderTop: '1px solid #2a2a3a', paddingTop: 10 },
  unitsBtn:     { background: '#1a2a4a', border: 'none', color: '#60a0f0', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer', flex: 1 },
  editBtn:      { background: '#2a2a4a', border: 'none', color: '#aaa', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' },
  deleteCardBtn:{ background: 'none', border: '1px solid #3a1a1a', color: '#f87171', borderRadius: 5, padding: '4px 8px', fontSize: 11, cursor: 'pointer' },
  table:        { width: '100%', borderCollapse: 'collapse' },
  th:           { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#555', borderBottom: '1px solid #252840' },
  tr:           { borderBottom: '1px solid #141420' },
  td:           { padding: '9px 12px', fontSize: 13, verticalAlign: 'middle' },
  actionBtn:    { background: '#2a2a4a', border: 'none', color: '#aaa', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer', marginRight: 4 },
  deleteBtn:    { background: 'none', border: '1px solid #3a1a1a', color: '#f87171', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' },
};
